import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ArrowLeft, ArrowUpCircle, ArrowDownCircle, FileBarChart, TrendingUp, TrendingDown, Equal, ArrowUp, ArrowDown, Minus, Receipt, FileArchive } from 'lucide-react';
import FacturesComptaTab from '../components/FacturesComptaTab';
import RapportsMensuelsTab from '../components/RapportsMensuelsTab';

export default function TableauComptable({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'factures' ? 'factures' : 'overview';
  const [loading, setLoading] = useState(true);
  const [financialData, setFinancialData] = useState(null);

  useEffect(() => {
    loadFinancialData();
  }, []);

  const loadFinancialData = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/financial/dashboard`);
      setFinancialData(response.data);
    } catch (error) {
      console.error('Error loading financial data:', error);
      setFinancialData(getDefaultFinancialData());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultFinancialData = () => ({
    currentMonth: {
      revenue: 25000,
      totalExpenses: 9000,
      netProfit: 16000
    },
    expenses: [
      { id: 1, category: 'Loyer et charges', amount: 2000 },
      { id: 2, category: 'Salaires', amount: 3500 },
      { id: 3, category: 'Frais de la mairie', amount: 500 },
      { id: 4, category: 'Frais de déplacement', amount: 800 },
      { id: 5, category: 'Assurance', amount: 400 },
      { id: 6, category: 'Frais entretien panneau', amount: 1200 },
      { id: 7, category: 'Marketing', amount: 600 }
    ],
    customExpenses: []
  });

  if (loading || !financialData) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  const allExpenses = [...(financialData.expenses || []), ...(financialData.customExpenses || [])];
  const totalExpenses = allExpenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
  const currentMonth = financialData.currentMonth || { revenue: 0, expenses: 0, profit: 0 };
  const benefice = currentMonth.revenue - totalExpenses;
  const isPositif = benefice >= 0;

  // Simulation de Produits (peut être enrichi par d'autres sources de revenus)
  const produits = [
    { label: "Chiffre d'affaires - Locations panneaux", amount: currentMonth.revenue }
  ];
  const totalProduits = produits.reduce((sum, p) => sum + p.amount, 0);

  const formatMontant = (n) => n.toLocaleString('fr-FR');

  // === Comparatif mois N-1 ===
  const monthlyTrend = financialData.monthlyTrend || [];
  // monthlyTrend est désormais toujours un tableau de 12 mois (Jan..Dec).
  // Le mois courant = index (mois actuel - 1). Le mois précédent = index - 1.
  const currentMonthIdx = new Date().getMonth();
  const prevIdx = currentMonthIdx > 0 ? currentMonthIdx - 1 : 11;
  const prevMonthData = monthlyTrend.length === 12 ? monthlyTrend[prevIdx] : null;
  const prevProduits = prevMonthData?.revenue ?? 0;
  const prevCharges = prevMonthData?.expenses ?? 0;
  const prevResultat = prevMonthData?.profit ?? (prevProduits - prevCharges);
  const prevMonthLabel = prevMonthData?.month || 'N-1';

  const computeVariation = (current, previous) => {
    const diff = current - previous;
    const pct = previous !== 0 ? (diff / Math.abs(previous)) * 100 : 0;
    return { diff, pct };
  };

  const varProduits = computeVariation(totalProduits, prevProduits);
  const varCharges = computeVariation(totalExpenses, prevCharges);
  const varResultat = computeVariation(benefice, prevResultat);

  const VariationBadge = ({ variation, inverseColor = false }) => {
    if (Math.abs(variation.diff) < 0.01) {
      return (
        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
          <Minus className="h-3 w-3" /> 0%
        </span>
      );
    }
    // Pour les charges, une baisse (diff<0) est positive (vert) : inverseColor=true
    const isFavorable = inverseColor ? variation.diff < 0 : variation.diff > 0;
    const colorClass = isFavorable ? 'text-green-600' : 'text-red-600';
    const Icon = variation.diff > 0 ? ArrowUp : ArrowDown;
    return (
      <span className={`inline-flex items-center gap-1 text-xs font-semibold ${colorClass}`}>
        <Icon className="h-3 w-3" />
        {variation.pct >= 0 ? '+' : ''}{variation.pct.toFixed(1)}%
        <span className="text-gray-500 font-normal ml-1">
          ({variation.diff >= 0 ? '+' : ''}{formatMontant(Math.round(variation.diff))}€)
        </span>
      </span>
    );
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            onClick={() => {
              const isInvestor = user?.role === 'investisseur' || user?.role === 'associe';
              navigate(-1);
            }}
            variant="outline"
            className="gap-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">💼 Tableau Comptable</h1>
            <p className="text-gray-600 mt-1">Vue détaillée des produits et charges</p>
          </div>
        </div>

        <Tabs defaultValue="factures" className="w-full">
          <TabsList className="grid w-full md:w-auto md:inline-grid grid-cols-2 mb-4">
            <TabsTrigger value="factures" data-testid="tab-factures" className="gap-2">
              <Receipt className="h-4 w-4" /> Factures
            </TabsTrigger>
            <TabsTrigger value="rapports" data-testid="tab-rapports" className="gap-2">
              <FileArchive className="h-4 w-4" /> Rapports mensuels
            </TabsTrigger>
          </TabsList>

          <TabsContent value="factures" className="space-y-6">
            <FacturesComptaTab />
          </TabsContent>

          <TabsContent value="rapports" className="space-y-6">
            <RapportsMensuelsTab user={user} />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
